import logging from 'utils/logging';
import { GAMESTATE_ROUND, GAMESTATE_ROUNDSTATE_QUESTION_SELECTED, GAMESTATE_ROUNDSTATE_DAILYDOUBLE_PICKED } from 'features/gamestate/constants';
import { HOST_STATUS, MOMENT_TYPES } from '../hostStateSlice';
import { advanceState } from '../../gamestate/GameState';
import { updateGameStateAction } from '../../gamestate/stateActions';
import { registerCacheReset } from '../momentCacheManager';

const log = logging.getLogger('moments.QuestionSelected');
log.setLevel('info');

const questionsSelected = new Set();

const questionSelectedPlayed = new Set();

export const resetQuestionSelectedCache = () => {
  questionsSelected.clear();
  questionSelectedPlayed.clear();
};
registerCacheReset('round', resetQuestionSelectedCache);

export const QuestionSelectedMoment = {
    id: 'QUESTION_SELECTED',
    shouldTrigger: ({ prevState, currentState, questions, currentPlayers }) => {
        if (currentState?.currentState !== GAMESTATE_ROUND) {
            return false;
        }

        if (currentState?.roundState !== GAMESTATE_ROUNDSTATE_QUESTION_SELECTED) {
            return false;
        }

        const questionId = currentState?.questionId;

        if (!questionId) {
            return false;
        }

        if (!questions || !questions.find(q => q.id === questionId)) {
            return false;
        }

        if (!currentPlayers || !currentPlayers.find(p => p.id === currentState.playerInControl)) {
            return false;
        }

        if (questionsSelected.has(questionId)) {
            return false;
        }

        questionsSelected.add(questionId);

        log.debug(`Question selected: ${questionId}`);

        const question = questions.find(q => q.id === questionId);

        if (!question) {
            return false;
        }

        const playerId = currentState?.playerInControl;
        const player = currentPlayers.find(p => p.id === playerId);

        const isDailyDouble = question.dailyDouble || false;
        const narrationKey = `QUESTION_SELECTED_${currentState?.questionId}`;

        return {
            shouldTriggerAction: false,
            shouldGenerateNarration: true,
            narrationParams: {
                narrationKey,
                momentType: MOMENT_TYPES.QUESTION_SELECTED,
                context: {
                    questionId,
                    category: question.category,
                    value: question.value,
                    playerName: player.name,
                    dailyDouble: isDailyDouble,
                }
            }
        };
    }
}

export const QuestionSelectedPlaybackMoment = {
    id: 'QUESTION_SELECTED_PLAYBACK',
    narrationKey: 'QUESTION_SELECTED',
    momentType: MOMENT_TYPES.QUESTION_SELECTED,
    shouldPlay: (gameState, hostState, narration, playedNarrations) => {
        if (!narration) {
            return false;
        }

        const questionId = gameState?.questionId;
        const narrationKey = narration?.key;

        if (narration.momentType !== MOMENT_TYPES.QUESTION_SELECTED) {
            return false;
        }

        if (gameState?.currentState !== GAMESTATE_ROUND || gameState?.roundState !== GAMESTATE_ROUNDSTATE_QUESTION_SELECTED) {
            return false;
        }

        if (questionSelectedPlayed.has(narrationKey)) {
            return false;
        }

        if (questionId !== narration?.context?.questionId) {
            return false;
        }

        if (hostState?.status === HOST_STATUS.SPEAKING) {
            return false;
        }

        const narrationReady = narration.textStatus === "READY" && (narration.audioStatus === "READY" || narration.audioStatus === "ERROR");
        if (!narrationReady) {
            return false;
        }

        const hostAvailable = hostState?.status !== HOST_STATUS.SPEAKING;

        if (!hostAvailable) {
            return false;
        }

        questionSelectedPlayed.add(narrationKey);
        return true;
    },

    onComplete: async (gameId, dispatch, getState) => {
        log.info('Question selected narration complete, checking for Daily Double');

        try {
            const state = getState();
            const gameObject = state.game?.game;

            if (!gameObject) {
                log.error('Game object not found in state');
                return;
            }

            const gameState = gameObject.state;
            const questionId = gameState.questionId;
            const questions = Object.values(state.questions?.entities || {});
            const question = questions.find(q => q.id === questionId);

            if (question?.dailyDouble) {
                // DD: transition to dailyDouble.picked WITH dailyDouble flag
                log.info(`Question ${questionId} is a Daily Double, transitioning to dailyDouble.picked`);
                dispatch(updateGameStateAction(gameId, {
                    ...gameState,
                    roundState: GAMESTATE_ROUNDSTATE_DAILYDOUBLE_PICKED,
                    dailyDouble: true,
                }));
            } else {
                // Normal: advance to question.reading
                log.info("Question selected narration complete. Advancing state to reading.");
                const players = Object.values(state.player?.entities || {});
                await advanceState(gameObject, players, dispatch);
            }
        } catch (error) {
            log.error('Error advancing game state:', error);
        }
    }
}


const api = {
    trigger: QuestionSelectedMoment,
    playback: QuestionSelectedPlaybackMoment
};

export default api;
